import { stripMarkdownAndHtml } from './stripMarkdown';

/**
 * Transforms an outcomeQ object to use French translations when available.
 * Maintains the exact same structure, replacing:
 * - Top-level `name` with `fr_name` if available
 * - Question `name` with `fr_name` if available
 * - Question `question` with `fr` if available
 * - Question `scale` with `fr_scale` if available and has non-empty values
 * 
 * @param {Object} outcomeQ - The outcome question object to transform
 * @returns {Object} - The transformed object with French translations, or original if no French translations available
 */
export const transformOutcomeQToFrench = (outcomeQ) => {
  if (!outcomeQ || !outcomeQ.questions) {
    return outcomeQ;
  }

  return {
    ...outcomeQ,
    name: outcomeQ.fr_name || outcomeQ.name,
    questions: outcomeQ.questions.map((question) => ({
      ...question,
      name: question.fr_name || question.name,
      questions: question.questions?.map((q) => ({
        ...q,
        question: q.fr && q.fr.length > 0 ? stripMarkdownAndHtml(q.fr) : q.question,
        scale: q.fr_scale && q.fr_scale.some(s => s.trim() !== '') ? q.fr_scale : q.scale,
      })) || [],
    })),
  };
};


/**
 * Transforms a question structure object to use French translations when available.
 * Maintains the exact same structure, replacing:
 * - Dimension `title` with `fr_title` if available
 * - Factor `title` with `fr_title` if available
 * - Question `q` with `fr` if available (with markdown stripped)
 * - Comment `question` with `fr` if available (with markdown stripped)
 * 
 * @param {Object} questionStructure - The question structure object to transform
 * @returns {Object} - The transformed object with French translations, or original if no French translations available
 */
export const transformQuestionStructureToFrench = (questionStructure) => {
  if (!questionStructure) {
    return questionStructure;
  }

  return {
    ...questionStructure,
    dimensions: questionStructure.dimensions?.map((dimension) => ({
      ...dimension,
      title: dimension.fr_title || dimension.title,
      factors: dimension.factors?.map((factor) => ({
        ...factor,
        title: factor.fr_title || factor.title,
        questions: factor.questions?.map((question) => ({
          ...question,
          q: question.fr && question.fr.length > 0 ? stripMarkdownAndHtml(question.fr) : question.q,
        })) || [],
      })) || [],
    })) || [],
    comments: questionStructure.comments?.map((comment) => ({
      ...comment,
      question: comment.fr && comment.fr.length > 0 ? stripMarkdownAndHtml(comment.fr) : comment.question,
    })) || [],
  };
};

/**
 * Transforms a categories array to use French translations when available.
 * Maintains the exact same structure, replacing:
 * - Category `name` with `fr_name` if available and has length > 0
 * - Option `name` with `fr` if available and has length > 0
 * 
 * @param {Array} categories - The categories array to transform
 * @returns {Array} - The transformed array with French translations, or original if no French translations available
 */
export const transformCategoriesToFrench = (categories) => {
    if (!Array.isArray(categories) || categories.length === 0) {
        return categories;
    }

    return categories.map((category) => ({
        ...category,
        // Use fr_name if available and has length > 0, otherwise use name
        name: (category.fr_name && category.fr_name.length > 0) ? category.fr_name : category.name,
        options: category.options?.map((option) => ({
            ...option,
            // Use fr if available and has length > 0, otherwise use name
            name: (option.fr && option.fr.length > 0) ? option.fr : option.name,
        })) || [],
    }));
};

